// OCR result -> NetSuite bill payload, plus the validation and bookkeeping the
// bill editor needs.
//
// The NetSuite bill endpoint owns every business rule (does the vendor exist,
// is the item valid for the subsidiary, …). Everything here is only about
// handing the user a sensible draft to review, catching obviously incomplete
// input before a round-trip, and reading the rejections the user can act on.

import { NETSUITE_FIXED } from '../config';
import {
  NetSuiteBillItem,
  NetSuiteBillPayload,
  NetSuiteBillTextField,
  NetSuitePurchaseOrderLine,
  NetSuiteReviewRequired,
  NetSuiteUnresolvedNames,
} from '../types';

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
// NFKD splits "Č" into "C" plus a combining mark, which the strip then drops —
// so "Číslo faktury" and "Cislo faktury" normalise alike.
const norm = (k: string) => k.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Document types that can never be a vendor bill. */
const NON_BILL_DOC_TYPES = new Set(['business_card', 'id_document']);

export const canCreateBill = (docType: string) => !NON_BILL_DOC_TYPES.has(docType);

// --- reading OCR data ----------------------------------------------------

// Generic mode returns labels as printed, so common Czech / German ones are listed too.
const VENDOR_NAME_KEYS = [
  'vendorName',
  'vendor',
  'supplierName',
  'supplier',
  'merchantName',
  'seller',
  'sellerName',
  'issuer',
  'billFrom',
  'dodavatel',
  'lieferant',
];
const INVOICE_NUMBER_KEYS = [
  'vendorInvoiceNumber',
  'invoiceNumber',
  'billNumber',
  'invoiceNo',
  'invoiceNum',
  'invoiceId',
  'invoice',
  'billNo',
  'taxInvoiceNumber',
  'receiptNumber',
  'documentNumber',
  'cisloFaktury',
  'cisloDokladu',
  'rechnungsnummer',
];
const PO_NUMBER_KEYS = [
  'purchaseOrderNumber',
  'poNumber',
  'orderNumber',
  'purchaseOrder',
  'poNo',
  'orderNo',
  'purchaseOrderNo',
  'cisloObjednavky',
  'objednavka',
  'bestellnummer',
];
const MEMO_KEYS = ['memo', 'notes', 'remarks'];

/**
 * Key lookup by normalised name. Covers every preset (invoice, bill, receipt),
 * custom schemas with similar names, and generic mode, whose values sit in a
 * `fields: [{ key, value }]` list with human labels like "Invoice No.".
 */
function indexFields(data: Obj): Map<string, unknown> {
  const index = new Map<string, unknown>();
  for (const [k, v] of Object.entries(data)) index.set(norm(k), v);
  if (Array.isArray(data.fields)) {
    for (const f of data.fields) {
      if (isObj(f) && typeof f.key === 'string' && !index.has(norm(f.key))) {
        index.set(norm(f.key), f.value);
      }
    }
  }
  return index;
}

function pick(index: Map<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const v = index.get(norm(key));
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    // Address-style objects: { name, line1, … }
    if (isObj(v) && typeof v.name === 'string' && v.name.trim()) return v.name.trim();
  }
  return '';
}

/**
 * Parse an amount "as printed" into a number:
 *   "1 234,50 Kč" -> 1234.5   "$1,234.50" -> 1234.5   "(12.00)" -> -12
 * Returns NaN when there is no number to read.
 */
export function parseAmount(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw !== 'string') return NaN;
  const text = raw.trim();
  // First digit through last digit, so a prefix's dot can't pose as a decimal
  // point: "Rs.4,720.00" -> "4,720.00", "1.00 pcs" -> "1.00".
  const match = /\d(?:[\d.,'’\s]*\d)?/.exec(text);
  if (!match) return NaN;

  const negative =
    /^\(.*\)$/.test(text) || text.slice(0, match.index).includes('-') || /\d\s*-$/.test(text);

  // Drops spaces / NBSP and apostrophe grouping (Swiss 1'234.50).
  let s = match[0].replace(/[^\d.,]/g, '');
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');

  if (lastDot !== -1 && lastComma !== -1) {
    // Both present: whichever comes last is the decimal separator.
    const decimal = lastDot > lastComma ? '.' : ',';
    const grouping = decimal === '.' ? ',' : '.';
    s = s.split(grouping).join('').replace(decimal, '.');
  } else if (lastDot !== -1 || lastComma !== -1) {
    const sep = lastComma !== -1 ? ',' : '.';
    const parts = s.split(sep);
    // "1.234.567" is grouping; so is "1,500" (a lone comma before exactly three
    // digits). Anything else — "12,50", "0,500", "1.5" — is a decimal point.
    const isGrouping =
      parts.length > 2 || (sep === ',' && parts[1].length === 3 && /^[1-9]\d{0,2}$/.test(parts[0]));
    s = isGrouping ? parts.join('') : `${parts.slice(0, -1).join('')}.${parts[parts.length - 1]}`;
  }

  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return NaN;
  return negative ? -n : n;
}

const round = (n: number, dp: number) => Math.round(n * 10 ** dp) / 10 ** dp;

// --- line items ------------------------------------------------------------
//
// Lines arrive under different names depending on how the document was read:
// presets give `lineItems: [{ description, quantity, rate }]`, custom schemas
// whatever the user named things, and generic mode a table of printed headers,
// `tables: [{ headers: ["Item & Description", "Qty", "Rate"], rows: [[…]] }]`.
// So columns are recognised by what their name says rather than exact keys.

type LineRole = 'itemName' | 'description' | 'quantity' | 'rate' | 'amount';
type Row = Record<string, unknown>;

const SKIP_COLUMN = ['sku', 'code', 'hsn', 'itemno', 'itemnumber', 'partno', 'cislo', 'discount', 'sleva', 'rabatt'];
const TAX_COLUMN = ['tax', 'vat', 'gst', 'dph', 'mwst'];
const EXCLUDES_TAX = ['excl', 'without', 'before', 'bez', 'ohne', 'net'];

// First match wins, so order matters: "Unit price" is a rate and "Total price"
// an amount although both say "price"; "Service description" is a description
// while a bare "Service" is the item's name.
const COLUMN_ROLES: [LineRole, string[]][] = [
  ['quantity', ['qty', 'quantity', 'units', 'hours', 'hrs', 'mnozstvi', 'pocet', 'menge', 'anzahl']],
  ['rate', ['unitprice', 'unitcost', 'priceper', 'perunit', 'rate', 'jednotk', 'zamj', 'einzelpreis']],
  ['amount', ['amount', 'total', 'celk', 'castka', 'zaklad', 'betrag', 'gesamt']],
  ['rate', ['price', 'cost', 'cena', 'preis']],
  ['description', ['desc', 'popis', 'beschreib', 'particular', 'leistung']],
  [
    'itemName',
    ['itemname', 'item', 'product', 'service', 'artikel', 'bezeichnung', 'nazev', 'oznaceni', 'polozk', 'zbozi'],
  ],
];
const EXACT_ROLES: [LineRole, string[]][] = [
  ['itemName', ['name']],
  ['description', ['text', 'detail', 'details']],
];
const SUMMARY_ROWS = ['total', 'subtotal', 'grandtotal', 'totalamount', 'celkem', 'summe', 'gesamt'];

const includesAny = (key: string, parts: string[]) => parts.some((part) => key.includes(part));

/** Which bill-line value a column holds, judged from its key or printed header. */
function columnRole(label: string): LineRole | null {
  const key = norm(label);
  if (!key || includesAny(key, SKIP_COLUMN)) return null;
  // Tax columns never hold the line's own values — unless explicitly net of tax ("Celkem bez DPH").
  if (includesAny(key, TAX_COLUMN) && !includesAny(key, EXCLUDES_TAX)) return null;
  const match = COLUMN_ROLES.find(([, parts]) => includesAny(key, parts));
  if (match) return match[0];
  const exact = EXACT_ROLES.find(([, keys]) => keys.includes(key));
  return exact ? exact[0] : null;
}

const isGenericTable = (v: unknown): v is Obj => isObj(v) && Array.isArray(v.rows);

/** A generic-mode table as row objects keyed by header. */
function tableRows(table: Obj): Row[] {
  const headers = Array.isArray(table.headers) ? table.headers.map((h) => String(h ?? '')) : [];
  const rows = Array.isArray(table.rows) ? table.rows.filter(Array.isArray) : [];
  return rows.map((cells) => {
    const row: Row = {};
    headers.forEach((header, i) => {
      if (!(header in row)) row[header] = cells[i];
    });
    return row;
  });
}

const LINE_LIST_KEYS = ['lineitems', 'items', 'lines'];

/** Every list that could hold the lines, up to one level deep — conventionally named lists first. */
function candidateLists(data: Obj): Row[][] {
  const named: Row[][] = [];
  const others: Row[][] = [];
  const visit = (key: string, value: unknown, depth: number) => {
    if (Array.isArray(value)) {
      if (value.some(isGenericTable)) {
        value.filter(isGenericTable).forEach((table) => others.push(tableRows(table)));
      } else if (value.some(isObj)) {
        (LINE_LIST_KEYS.includes(norm(key)) ? named : others).push(value.filter(isObj));
      }
    } else if (isObj(value) && depth === 0) {
      Object.entries(value).forEach(([k, v]) => visit(k, v, 1));
    }
  };
  Object.entries(data).forEach(([k, v]) => visit(k, v, 0));
  return [...named, ...others];
}

/** Lines from the first list that has a description column and a rate or amount column. */
function mapItems(data: Obj): NetSuiteBillItem[] {
  for (const rows of candidateLists(data)) {
    const columns: Partial<Record<LineRole, string>> = {};
    rows.forEach((row) =>
      Object.keys(row).forEach((key) => {
        const role = columnRole(key);
        if (role && !columns[role]) columns[role] = key;
      })
    );
    if (!(columns.description || columns.itemName) || !(columns.rate || columns.amount)) continue;

    const cell = (row: Row, role: LineRole) => {
      const key = columns[role];
      const value = key ? row[key] : undefined;
      return value === null || value === undefined ? '' : String(value).trim();
    };

    const items: NetSuiteBillItem[] = [];
    rows.forEach((row) => {
      const description = cell(row, 'description');
      const itemName = cell(row, 'itemName');
      const text = itemName || description;
      const parsedQty = parseAmount(cell(row, 'quantity'));
      const quantity = Number.isFinite(parsedQty) && parsedQty !== 0 ? parsedQty : 1;

      const printedRate = parseAmount(cell(row, 'rate'));
      const amount = parseAmount(cell(row, 'amount'));
      let rate = printedRate;
      // The line amount is what gets posted, so it wins: derive the rate from it
      // when none was printed, or when a printed unit price times the quantity
      // disagrees with it (vendors round unit prices — one invoice was 2 cents
      // out). 8 decimals keeps quantity × rate on the printed amount; 4 let a
      // 72-line invoice drift by 12 cents.
      const amountDisagrees = Number.isFinite(amount) && Math.abs(quantity * rate - amount) > 0.005;
      if (!Number.isFinite(rate) || amountDisagrees) {
        rate = Number.isFinite(amount) ? round(amount / quantity, 8) : printedRate;
      }

      // Skip blank rows and the "Total" rows tables often end with.
      if ((!text && !Number.isFinite(rate)) || SUMMARY_ROWS.includes(norm(text))) return;
      // A line always carries both texts: when the document prints only one,
      // the item name and the description are the same thing. Vendor SKUs are
      // not NetSuite internal IDs, so the item id starts empty.
      items.push({ itemId: '', itemName: text, description: description || itemName, quantity, rate });
    });
    if (items.length) return items;
  }
  return [];
}

export interface ExtractedTotals {
  currency: string;
  /** Net total before tax, if the document shows one. */
  subtotal: number;
  total: number;
}

/** Symbols and local spellings mapped to ISO 4217 codes. */
const CURRENCY_CODES: Record<string, string> = {
  '€': 'EUR',
  eur: 'EUR',
  euro: 'EUR',
  $: 'USD',
  us$: 'USD',
  usd: 'USD',
  '£': 'GBP',
  gbp: 'GBP',
  '₹': 'INR',
  rs: 'INR',
  inr: 'INR',
  kc: 'CZK',
  czk: 'CZK',
  zł: 'PLN',
  zl: 'PLN',
  pln: 'PLN',
  ft: 'HUF',
  huf: 'HUF',
  chf: 'CHF',
  sfr: 'CHF',
  '¥': 'JPY',
  jpy: 'JPY',
  lv: 'BGN',
  bgn: 'BGN',
  lei: 'RON',
  ron: 'RON',
};

/** "Kč" -> "CZK", "€" -> "EUR", "usd" -> "USD". Empty when it can't be told. */
export function normalizeCurrency(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const text = raw.trim();
  if (!text) return '';
  if (CURRENCY_CODES[text]) return CURRENCY_CODES[text];
  if (/^[A-Za-z]{3}$/.test(text)) return text.toUpperCase();
  // Strip accents and punctuation: "Kč." and "Kc" both land on "kc". Letters
  // that don't decompose into ASCII ("ł") are replaced first.
  const letters = text
    .normalize('NFKD')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/[^a-z]/g, '');
  if (CURRENCY_CODES[letters]) return CURRENCY_CODES[letters];
  return letters.length === 3 ? letters.toUpperCase() : '';
}

export function extractTotals(data: unknown): ExtractedTotals {
  const index = indexFields(isObj(data) ? data : {});
  return {
    currency: normalizeCurrency(pick(index, ['currency', 'mena', 'wahrung'])),
    subtotal: parseAmount(
      pick(index, ['subTotal', 'netTotal', 'totalNet', 'totalExclTax', 'totalBeforeTax', 'celkemBezDph'])
    ),
    total: parseAmount(
      pick(index, ['total', 'grandTotal', 'totalAmount', 'amountDue', 'totalAmountDue', 'celkemKUhrade'])
    ),
  };
}

// --- remembered values ---------------------------------------------------

const MEMORY_KEY = 'aiOcrReader.netsuite.v1';
const MAX_REMEMBERED_VENDORS = 300;

interface Memory {
  /** normalised vendor name -> NetSuite vendor entity ID */
  vendors?: Record<string, string>;
}

// localStorage can throw (privacy mode, blocked site data) — never let that
// break bill creation.
function readMemory(): Memory {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MEMORY_KEY) || '{}');
    return isObj(parsed) ? (parsed as Memory) : {};
  } catch {
    return {};
  }
}

/** Called after a successful create, so this vendor's next bill starts pre-filled. */
export function rememberBill(payload: NetSuiteBillPayload): void {
  const vendors = { ...(readMemory().vendors || {}) };
  const vendorKey = norm(payload.vendorName);
  if (vendorKey && payload.vendorEntityId.trim()) {
    delete vendors[vendorKey]; // re-insert so the most recent sits last
    vendors[vendorKey] = payload.vendorEntityId.trim();
  }
  const vendorKeys = Object.keys(vendors);
  vendorKeys.slice(0, Math.max(0, vendorKeys.length - MAX_REMEMBERED_VENDORS)).forEach((k) => {
    delete vendors[k];
  });
  try {
    window.localStorage.setItem(MEMORY_KEY, JSON.stringify({ vendors }));
  } catch {
    /* storage unavailable — nothing to remember */
  }
}

export function rememberedVendorId(vendorName: string): string {
  const key = norm(vendorName);
  return (key && readMemory().vendors?.[key]) || '';
}

// --- building & sending the payload ----------------------------------------

// NFKD splits accents off ("ě" -> "e" + mark) and dropping everything outside
// printable ASCII removes the marks: "Brněnská" -> "brnenska".
const slug = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/[^ -~]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * A new externalId — different on every call, also for the same invoice sent
 * again. The invoice number is kept in it so the bill is easy to find.
 */
let externalIdCounter = 0;

export function newExternalId(vendorInvoiceNumber: string): string {
  // The timestamp keeps ids sortable, the counter makes two ids in the same
  // millisecond impossible to collide (4 random characters alone did collide),
  // and the random tail keeps two browser tabs apart.
  const unique = [
    Date.now().toString(36),
    (externalIdCounter++).toString(36),
    Math.random().toString(36).slice(2, 6).padEnd(4, '0'),
  ].join('');
  return ['ocr-bill', slug(vendorInvoiceNumber).slice(0, 40), unique].filter(Boolean).join('-');
}

/** Adds review checks the user accepted to any already in the payload. */
export function withConfirmedChecks(p: NetSuiteBillPayload, ids: string[]): NetSuiteBillPayload {
  const existing = Array.isArray(p.confirmedChecks) ? p.confirmedChecks : [];
  const checks = Array.from(new Set([...existing, ...ids]));
  return checks.length ? { ...p, confirmedChecks: checks } : p;
}

/** externalIds already sent from this page — no attempt may reuse one. */
const sentExternalIds = new Set<string>();

/**
 * The exact body for one send: fixed subsidiary and requestor, an externalId
 * never sent before, and the accepted review checks. Marks the externalId used.
 */
export function prepareBillRequest(draft: NetSuiteBillPayload, confirmedChecks: string[] = []): NetSuiteBillPayload {
  const current = draft.externalId.trim();
  const request: NetSuiteBillPayload = {
    ...withConfirmedChecks(draft, confirmedChecks),
    ...NETSUITE_FIXED,
    externalId: current && !sentExternalIds.has(current) ? current : newExternalId(draft.vendorInvoiceNumber),
  };
  // An unknown currency is left out rather than sent empty, so NetSuite can
  // fall back to the vendor's or the purchase order's own currency.
  const currency = normalizeCurrency(request.currency);
  if (currency) request.currency = currency;
  else delete request.currency;
  sentExternalIds.add(request.externalId);
  return request;
}

export function mapOcrToBill(data: unknown, filename: string): NetSuiteBillPayload {
  const obj = isObj(data) ? data : {};
  const index = indexFields(obj);
  const vendorName = pick(index, VENDOR_NAME_KEYS);
  const vendorInvoiceNumber = pick(index, INVOICE_NUMBER_KEYS);

  return {
    purchaseOrderNumber: pick(index, PO_NUMBER_KEYS),
    subsidiaryId: NETSUITE_FIXED.subsidiaryId,
    vendorEntityId: rememberedVendorId(vendorName),
    vendorName,
    vendorInvoiceNumber,
    currency: normalizeCurrency(pick(index, ['currency', 'mena', 'wahrung'])),
    memo: pick(index, MEMO_KEYS) || `Created by AI OCR Reader from ${filename}`,
    requestorId: NETSUITE_FIXED.requestorId,
    externalId: newExternalId(vendorInvoiceNumber),
    items: mapItems(obj),
  };
}

export const emptyBillItem = (): NetSuiteBillItem => ({
  itemId: '',
  itemName: '',
  description: '',
  quantity: 1,
  rate: NaN,
});

export const lineAmount = (item: NetSuiteBillItem) => item.quantity * item.rate;

export function linesTotal(items: NetSuiteBillItem[]): number {
  return items.reduce((sum, item) => sum + lineAmount(item), 0);
}

/** NaN is how the form stores an empty number box; JSON can only say null. */
const toNumber = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim()) return Number(v);
  return NaN;
};
const toText = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

const TEXT_FIELDS: NetSuiteBillTextField[] = [
  'purchaseOrderNumber',
  'vendorEntityId',
  'vendorName',
  'vendorInvoiceNumber',
  'currency',
  'memo',
  'externalId',
];

/**
 * Turn JSON typed into the editor back into a payload. Known fields are
 * coerced to their expected types, the fixed ones reset; extra properties are
 * kept as-is.
 */
export function coercePayload(value: unknown): { payload: NetSuiteBillPayload } | { error: string } {
  if (!isObj(value)) return { error: 'The payload must be a JSON object.' };
  if (!Array.isArray(value.items)) return { error: '"items" must be an array.' };
  const badLine = value.items.findIndex((item) => !isObj(item));
  if (badLine !== -1) return { error: `items[${badLine}] must be an object.` };

  const payload = { ...value, ...NETSUITE_FIXED } as NetSuiteBillPayload;
  TEXT_FIELDS.forEach((field) => {
    payload[field] = toText(value[field]);
  });
  payload.items = (value.items as Obj[]).map((item) => {
    const line: NetSuiteBillItem = {
      ...item,
      itemId: toText(item.itemId),
      itemName: toText(item.itemName),
      description: toText(item.description),
      quantity: toNumber(item.quantity),
      rate: toNumber(item.rate),
    };
    // A mapped purchase order line is sent as a number.
    if (item.purchaseOrderLine !== undefined && item.purchaseOrderLine !== null) {
      line.purchaseOrderLine = toNumber(item.purchaseOrderLine);
    }
    return line;
  });
  return { payload };
}

// --- validation ----------------------------------------------------------

/**
 * Field path -> message. Paths: "vendorName", "items", "items.2.rate", …
 * Only checks what would certainly fail; NetSuite validates the rest.
 */
export type BillErrors = Record<string, string>;

export function validateBill(p: NetSuiteBillPayload): BillErrors {
  const errors: BillErrors = {};
  const blank = (s: string) => !s.trim();

  if (blank(p.vendorEntityId) && blank(p.vendorName)) {
    errors.vendorEntityId = 'Enter the vendor entity ID or the vendor name.';
  }
  if (blank(p.vendorInvoiceNumber)) errors.vendorInvoiceNumber = 'Vendor invoice number is required.';

  if (p.items.length === 0) errors.items = 'Add at least one line item.';
  p.items.forEach((item, i) => {
    if (blank(item.itemId) && blank(item.itemName)) {
      errors[`items.${i}.itemId`] = 'Item ID or item name is required.';
    }
    if (!Number.isFinite(item.quantity) || item.quantity === 0) {
      errors[`items.${i}.quantity`] = 'Enter a quantity.';
    }
    if (!Number.isFinite(item.rate)) errors[`items.${i}.rate`] = 'Enter a rate.';
  });
  return errors;
}

// --- rejections the user can act on --------------------------------------

/** The bill endpoint's `error` object from an API error's details, if it carries this code. */
function upstreamError(details: unknown, code: string): Obj | null {
  const response = isObj(details) ? details.response : undefined;
  if (!isObj(response) || !isObj(response.error) || response.error.code !== code) return null;
  return { ...response.error, requestId: response.requestId };
}

export function upstreamRequestId(details: unknown): string {
  const response = isObj(details) ? details.response : undefined;
  return isObj(response) && typeof response.requestId === 'string' ? response.requestId : '';
}

/** REVIEW_REQUIRED: differences the user may accept by resending with `confirmedChecks`. */
export function readReviewRequired(details: unknown): NetSuiteReviewRequired | null {
  const error = upstreamError(details, 'REVIEW_REQUIRED');
  if (!error || !Array.isArray(error.checks)) return null;
  const checks = error.checks.filter((c): c is Obj => isObj(c) && typeof c.id === 'string');
  return { ...error, checks } as unknown as NetSuiteReviewRequired;
}

/** UNRESOLVED_NAMES: vendor / item names that didn't match exactly one NetSuite record. */
export function readUnresolvedNames(details: unknown): NetSuiteUnresolvedNames | null {
  const error = upstreamError(details, 'UNRESOLVED_NAMES');
  if (!error) return null;
  const vendor = isObj(error.vendor) ? error.vendor : undefined;
  const candidates = vendor && Array.isArray(vendor.candidates) ? vendor.candidates : [];
  return {
    ...error,
    vendor: vendor && {
      ...vendor,
      candidates: candidates.filter((c) => isObj(c) && c.vendorId !== undefined && c.vendorId !== null),
    },
    items: Array.isArray(error.items)
      ? error.items.filter((item) => isObj(item) && typeof item.index === 'number')
      : [],
  } as unknown as NetSuiteUnresolvedNames;
}

/**
 * A name NetSuite couldn't resolve, as a form error. It stays visible only
 * until the user changes what it is about (`snapshot`), so a fixed field stops
 * looking broken.
 */
export interface NameIssue {
  /** Form error key: "vendorName", "items.1.itemName" */
  path: string;
  message: string;
  snapshot: string;
}

const vendorSnapshot = (p: NetSuiteBillPayload) =>
  JSON.stringify([p.vendorId ?? '', p.vendorEntityId, p.vendorName]);
const itemSnapshot = (p: NetSuiteBillPayload, i: number) =>
  JSON.stringify([p.items[i]?.itemId, p.items[i]?.itemName]);

export function nameIssues(names: NetSuiteUnresolvedNames, sent: NetSuiteBillPayload): NameIssue[] {
  const issues: NameIssue[] = [];
  if (names.vendor) {
    issues.push({ path: 'vendorName', message: names.vendor.message, snapshot: vendorSnapshot(sent) });
  }
  (names.items || []).forEach((item) => {
    const field = /itemId$/.test(item.field || '') ? 'itemId' : 'itemName';
    issues.push({
      path: `items.${item.index}.${field}`,
      message: item.message,
      snapshot: itemSnapshot(sent, item.index),
    });
  });
  return issues;
}

/** The issues the current draft still has, keyed like validation errors. */
export function openIssueErrors(issues: NameIssue[], p: NetSuiteBillPayload): BillErrors {
  const errors: BillErrors = {};
  issues.forEach((issue) => {
    const line = /^items\.(\d+)\./.exec(issue.path);
    const current = line ? itemSnapshot(p, Number(line[1])) : vendorSnapshot(p);
    if (current === issue.snapshot) errors[issue.path] = issue.message;
  });
  return errors;
}

// --- mapping bill lines onto purchase order lines -------------------------
//
// When NetSuite can't tell which purchase order line a bill line belongs to it
// returns a PURCHASE_ORDER_LINE_UNCLEAR check per line, each listing the order's
// lines as candidates. These are not confirmable: the request has to say
// `items[i].purchaseOrderLine`. Mapping takes NetSuite's item id and name from
// the chosen order line and leaves the invoice's quantity, rate and text alone.

// Two check shapes ask for the same fix. `items[0].purchaseOrderLine`
// (PURCHASE_ORDER_LINE_UNCLEAR) needs only the line number; `items[0].itemName`
// (ITEM_NOT_ON_PURCHASE_ORDER) needs the line number *and* its own id in
// `confirmedChecks`, which is what the endpoint's message asks for.
const LINE_NUMBER_CHECK_RE = /^items\[(\d+)\]\.purchaseOrderLine$/;
const ITEM_NAME_CHECK_RE = /^items\[(\d+)\]\.itemName$/;

export interface LineMappingCheck {
  checkId: string;
  itemIndex: number;
  candidates: NetSuitePurchaseOrderLine[];
  /** The check id also has to be confirmed once the line is matched. */
  needsConfirm: boolean;
}

/** item index -> purchase order line number */
export type LineMapping = Record<number, number>;

/** One order line, from either shape the endpoint sends. */
function readPurchaseOrderLine(raw: unknown): NetSuitePurchaseOrderLine | null {
  if (!isObj(raw)) return null;
  const number =
    typeof raw.purchaseOrderLine === 'number'
      ? raw.purchaseOrderLine
      : typeof raw.line === 'number'
        ? raw.line
        : NaN;
  if (!Number.isFinite(number)) return null;

  // `item` is either the name itself or { id, name }.
  const item = isObj(raw.item) ? raw.item : undefined;
  const name = item
    ? typeof item.name === 'string'
      ? item.name
      : ''
    : typeof raw.item === 'string'
      ? raw.item
      : '';
  const itemId = item?.id ?? raw.itemId;

  const line: NetSuitePurchaseOrderLine = { purchaseOrderLine: number };
  if (name) line.item = name;
  if (itemId !== undefined && itemId !== null) line.itemId = String(itemId);
  if (typeof raw.description === 'string') line.description = raw.description;
  (['quantity', 'rate', 'amount', 'remaining'] as const).forEach((key) => {
    const value = parseAmount(raw[key]);
    if (Number.isFinite(value)) line[key] = value;
  });
  if (typeof raw.open === 'boolean') line.open = raw.open;
  return line;
}

/** One entry per order line, merging what the different places tell us about it. */
function dedupeLines(lines: NetSuitePurchaseOrderLine[]): NetSuitePurchaseOrderLine[] {
  const byNumber = new Map<number, NetSuitePurchaseOrderLine>();
  lines.forEach((line) => {
    const existing = byNumber.get(line.purchaseOrderLine);
    byNumber.set(line.purchaseOrderLine, existing ? { ...existing, ...line } : line);
  });
  return Array.from(byNumber.values()).sort((a, b) => a.purchaseOrderLine - b.purchaseOrderLine);
}

const checkLines = (check: NetSuiteReviewRequired['checks'][number]) =>
  [...(check.candidates || []).map(readPurchaseOrderLine), readPurchaseOrderLine(check.purchaseOrder)].filter(
    (line): line is NetSuitePurchaseOrderLine => line !== null
  );

/** Checks fixed by matching a bill line to an order line rather than by confirming. */
export function lineMappingChecks(review: NetSuiteReviewRequired): LineMappingCheck[] {
  const checks: LineMappingCheck[] = [];
  review.checks.forEach((check) => {
    const byNumber = LINE_NUMBER_CHECK_RE.exec(check.id);
    const byName = ITEM_NAME_CHECK_RE.exec(check.id);
    const matched = byNumber || (byName && /PURCHASE_ORDER/.test(check.code) ? byName : null);
    if (!matched) return;
    checks.push({
      checkId: check.id,
      itemIndex: Number(matched[1]),
      // Only an explicit `candidates` list narrows this line's options. The
      // check's own `purchaseOrder` is just the line it was compared against —
      // treating that as the sole option would silently match every line to it.
      candidates: dedupeLines(
        (check.candidates || [])
          .map(readPurchaseOrderLine)
          .filter((line): line is NetSuitePurchaseOrderLine => line !== null)
      ),
      needsConfirm: Boolean(byName),
    });
  });
  return checks;
}

/** Every purchase order line on offer, from the order summary and from the checks. */
export function purchaseOrderLines(review: NetSuiteReviewRequired): NetSuitePurchaseOrderLine[] {
  const fromSummary = (review.purchaseOrder?.lines || [])
    .map(readPurchaseOrderLine)
    .filter((line): line is NetSuitePurchaseOrderLine => line !== null);
  const fromChecks = review.checks.flatMap(checkLines);
  return dedupeLines([...fromSummary, ...fromChecks]);
}

/** Check ids to confirm alongside the matched line numbers. */
export function mappingConfirmations(checks: LineMappingCheck[]): string[] {
  return checks.filter((check) => check.needsConfirm).map((check) => check.checkId);
}

/** With a single order line there is nothing to decide — map every bill line to it. */
export function initialLineMapping(
  checks: LineMappingCheck[],
  lines: NetSuitePurchaseOrderLine[]
): LineMapping {
  const mapping: LineMapping = {};
  checks.forEach((check) => {
    const options = check.candidates.length ? check.candidates : lines;
    if (options.length === 1) mapping[check.itemIndex] = options[0].purchaseOrderLine;
  });
  return mapping;
}

/**
 * Point the mapped bill lines at their order lines: the item id and name come
 * from NetSuite, while quantity, rate and the invoice's own text stay as read.
 */
export function applyLineMapping(
  p: NetSuiteBillPayload,
  mapping: LineMapping,
  lines: NetSuitePurchaseOrderLine[]
): NetSuiteBillPayload {
  const byNumber = new Map(lines.map((line) => [line.purchaseOrderLine, line]));
  return {
    ...p,
    items: p.items.map((item, i) => {
      const line = mapping[i] === undefined ? undefined : byNumber.get(mapping[i]);
      if (!line) return item;
      return {
        ...item,
        itemId: line.itemId === undefined || line.itemId === null ? item.itemId : String(line.itemId),
        itemName: line.item || item.itemName,
        description: item.description || item.itemName,
        purchaseOrderLine: line.purchaseOrderLine,
      };
    }),
  };
}

// --- presenting results --------------------------------------------------

export function formatMoney(n: number, currency?: string): string {
  if (!Number.isFinite(n)) return '—';
  const digits = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  if (currency && /^[A-Z]{3}$/.test(currency)) {
    try {
      return new Intl.NumberFormat(undefined, { ...digits, style: 'currency', currency }).format(n);
    } catch {
      /* unknown ISO code — fall through to a plain number */
    }
  }
  const plain = new Intl.NumberFormat(undefined, digits).format(n);
  return currency ? `${plain} ${currency}` : plain;
}

const RESULT_FIELDS: { label: string; keys: string[] }[] = [
  { label: 'NetSuite internal ID', keys: ['internalId', 'billId', 'recordId', 'id'] },
  { label: 'Transaction number', keys: ['tranId', 'transactionNumber', 'documentNumber', 'billNumber'] },
  { label: 'External ID', keys: ['externalId'] },
  { label: 'Status', keys: ['status', 'approvalStatus'] },
];

/**
 * Pull the headline facts out of the bill endpoint's response, whatever its
 * nesting (`{ id }`, `{ data: { internalId } }`, `{ bill: { tranId } }`, …).
 */
export function summarizeBillResponse(data: unknown): { label: string; value: string }[] {
  const scopes: Obj[] = [];
  const collect = (v: unknown, depth: number) => {
    if (!isObj(v) || depth > 2) return;
    scopes.push(v);
    Object.values(v).forEach((child) => collect(child, depth + 1));
  };
  collect(data, 0);

  const facts: { label: string; value: string }[] = [];
  RESULT_FIELDS.forEach(({ label, keys }) => {
    for (const scope of scopes) {
      const index = indexFields(scope);
      const value = pick(index, keys);
      if (value) {
        facts.push({ label, value });
        return;
      }
    }
  });
  return facts;
}

export function responseMessage(data: unknown): string {
  if (isObj(data) && typeof data.message === 'string') return data.message;
  if (isObj(data) && isObj(data.data) && typeof data.data.message === 'string') return data.data.message;
  return '';
}
